import React, { Fragment, useState, useEffect, useMemo } from "react";
import {
  Card,
  Tabs,
  Tab,
  Box,
  Grid,
  Select,
  MenuItem,
  Button,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import ShiftRosterTable from "./ShiftRosterTable";
import { Form, Formik } from "formik";
import dayjs from "dayjs";
import CardContent from "@/pages/attendanceManagement/components/CardContent";
import { useGetPayrollLockDateQuery } from "@/api/services/attendance/calendar/Calendar";
import { format, eachDayOfInterval, startOfMonth, endOfMonth, parse } from 'date-fns';
import { useGetDayTypeQuery, useGetShiftRosterQuery } from "@/api/services/attendanceManagement/shiftRoster/ShiftRoster";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import { StyledButton } from "@/components";
import useFetchCommonData from "@/hooks/useFetchCommonData";
import DataTable from "@/components/dataTableComponents/DataTable";

const DayType = () => {


  const [empId, setEmpId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("MM-YYYY"));

  // test
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataTableKey, setDataTableKey] = useState(0);

  const { data: fetchAllEmployees } = useFetchCommonData("EMPLOYEES");
  const employeeOptions =
    fetchAllEmployees &&
    fetchAllEmployees.map((item) => ({
      value: item.empId,
      label: item.employeeName,
    }));

  const { data: dayTypeDetails = [], refetch: refetchDayType, isUninitialized } =
    useGetDayTypeQuery(
      {
        ...(empId != null ? { empId } : {}), // Only include empId if it's NOT null or undefined
        month: selectedMonth,
      },
      { skip: !selectedMonth }
    );
  const rows = dayTypeDetails?.data || [];

  const flattenRowData = (row) => {
    return row.updatedDayType?.map(({ date, dayTypes }) => ({
      empId: row.empId || "",
      empName: row.empName || "",
      date,
      dayType: dayTypes?.dayType || "",
      shiftCode: dayTypes?.shiftCode || "",
    })) || [];
  };

  const flattenedRows = rows.flatMap(flattenRowData);

  useEffect(() => {
    if (!isUninitialized) {
      refetchDayType();
    }
  }, [selectedMonth, empId, refetchDayType, isUninitialized]);


  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset page on rows per page change
  };

  const filteredRows = rows;
  const totalRows = filteredRows.length;




  const columns = [
    { field: "empId", headerName: "Employee ID" },
    { field: "empName", headerName: "Employee Name" },
    { field: "date", headerName: "Date" },
    { field: "dayType", headerName: "Day Type" },
    { field: "shiftCode", headerName: "shift Code" },
  ];




  const [visibleColumns, setVisibleColumns] = useState([]);

  useEffect(() => {
    const newVisibleColumns = columns.map((column) => ({ ...column, visible: true }));

    // Only update state if there's a real change
    if (JSON.stringify(newVisibleColumns) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(newVisibleColumns);
    }
  }, [columns]); // Runs only when `columns` changes


  const handleToggleColumn = (field) => {
    setVisibleColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.field === field ? { ...column, visible: !column.visible } : column
      )
    );
  };

  const renderedColumns = visibleColumns.filter((column) => column.visible);


  useEffect(() => {
    setDataTableKey((prev) => prev + 1)
  }, [visibleColumns])



  return (
    <CardContent title="Date Type History">

      <Formik
        initialValues={{
          employee: "",
          startDate: selectedMonth ? moment(selectedMonth, "MM-YYYY").format("MMMM YYYY") : ""
        }}
        onSubmit={(values) => {
          setEmpId(values.employee || null);
          setSelectedMonth(values.startDate);
        }}
      >
        {({ setFieldValue, values, resetForm }) => (
          <Form>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormikSelectInput
                    name="employee"
                    label="Employee"
                    options={employeeOptions}
                    placeholder={`Select Employee`}
                    width="100%"
                    search={true}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue("employee", value);
                      setEmpId(value || null);
                    }}
                  />
                  <FormikMonthYearPicker
                    name="startDate"
                    label="Month Year"
                    pickerType="month"
                    placeholder="Select"
                    value={values.startDate ? moment(values.startDate, "MM-YYYY").format("MMMM YYYY") : ""}
                    onChange={(formattedDate) => {
                      const formatted = formattedDate
                        ? moment(formattedDate).format("MM-YYYY")
                        : "";
                      setFieldValue("startDate", formatted);
                      setSelectedMonth(formatted);
                    }}
                    maxDate={new Date()}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} container justifyContent="flex-end">
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                  <StyledButton
                    sx={{ p: (theme) => theme.spacing(1, 2), m: (theme) => theme.spacing(0, 1), borderRadius: (theme) => theme.shape.s, height: 40 }}
                    type="reset"
                    label="Reset"
                    variant="outlined"
                    color="error"
                    size="medium"
                    onClick={() => {
                      resetForm();
                      setSelectedMonth(moment().format("MM-YYYY"));
                      setEmpId(null)
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>



      <DataTable
        key={dataTableKey}
        rows={flattenedRows}
        visibleColumns={visibleColumns}
        columns={renderedColumns}
        colvisColumns={columns}
        handleToggleColumn={handleToggleColumn}
        checkboxSelection={false}
        headerBgColor="#EAF5EE"
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        index={true}
      />

    </CardContent>
  );
};

export default DayType;
